"use client";

import type { FooterData, FooterGroup, LanguageOption } from "../../../types";
import { RuntimeImage, useLocale } from "../../../runtime/RendererRuntime";
import { Plus, Minus } from "lucide-react";
import { GuardedLink, useDevPlaceholder } from "../../../runtime/DevPlaceholder";
import { t } from "../../../i18n/strings";
import { resolveConfiguredHref } from "../../../utils/links";
import { SocialDiscRow } from "./SocialDiscRow";
import { FooterLanguageLinks } from "./FooterLanguageLinks";

/**
 * Mobile footer — vertical stack mirroring the desktop content order so
 * editorial changes propagate to both viewports without per-viewport edits:
 *   1. Brand + addresses
 *   2. Visitez aussi nos pages (collapsible)
 *   3. Informations pratiques (collapsible)
 *   4. Contact note paragraph
 *   5. Yellow social row
 *
 * Audiences are intentionally not rendered on either viewport now; the field
 * remains in `FooterData` so other surfaces (or a future redesign) can use it.
 */
export function SiteFooterMobile({
	data,
	logoSrc,
	logoAlt,
	language,
	languageOptions,
	onLanguageChange,
	resolveHref,
}: {
	data: FooterData;
	logoSrc: string;
	logoAlt: string;
	language?: string;
	languageOptions?: LanguageOption[];
	onLanguageChange?: (code: string) => void;
	resolveHref?: (code: string) => string;
}) {
	const { previewMode } = useDevPlaceholder();
	const locale = useLocale();
	const resourcesGroup = pickGroup(data.groups, ["Visitez aussi nos pages", "Visitez aussi"], 0);
	const practicalGroup = pickGroup(data.groups, ["Informations pratiques"], 1);
	const contactNote = (data.contactNote ?? "").trim();
	// Même règle que le desktop (SiteFooterDesktop) : la production cache les liens sans
	// destination, la preview les garde (clic = modal dev).
	const legalLinks = data.legalLinks.filter((link) => previewMode || resolveConfiguredHref(link.href));

	return (
		// Sert jusqu'à lg (1024px), pas seulement sur mobile : dès que le pied de
		// page bureau perd ses colonnes, cette variante en accordéon est plus
		// lisible qu'un empilement de blocs — voir SiteFooterDesktop.
		<div className="lg:hidden">
			<div className="rounded-lg bg-[#005CA9] p-4">
				{/* Lockup blanc sur une ligne : hauteur plafonnée à 40px sur mobile, largeur au ratio. */}
				<RuntimeImage src={logoSrc} alt={logoAlt} className="h-auto max-h-[40px] w-auto max-w-full object-contain object-left" />
				<div className="mt-4 space-y-3 text-base xl:text-lg text-white/80">
					{data.addresses.map((addr) => (
						<div key={addr.title}>
							<p className="font-semibold text-white">{addr.title}</p>
							{addr.lines.map((l, idx) => (
								<p key={idx}>{l}</p>
							))}
							{(addr.actions ?? []).filter((a) => previewMode || resolveConfiguredHref(a.href)).map((a) => (
								<GuardedLink
									key={a.label}
									label={a.label}
									className="inline-block rounded-full bg-white/10 px-2 py-1 text-xs xl:text-sm hover:bg-white/15"
									href={a.href}
								>
									{a.label}
								</GuardedLink>
							))}
						</div>
					))}
				</div>
			</div>

			<div className="mt-4 space-y-3">
				<MobileGroup group={resourcesGroup} fallbackTitle="Visitez aussi nos pages" />
				<MobileGroup group={practicalGroup} fallbackTitle="Informations pratiques" />
			</div>

			{contactNote && (
				<p className="mt-4 rounded-lg border border-white/15 bg-[#005CA9] p-4 text-sm xl:text-base leading-relaxed text-white/90">
					{contactNote}
				</p>
			)}

			{/* Mobile-only: center the social row and give it generous top/bottom
			    padding so it doesn't feel cramped between the contact note and the
			    legal line. Desktop keeps left-aligned, tighter spacing — see
			    SiteFooterDesktop.tsx. */}
			<div className="mt-2 py-6">
				<SocialDiscRow items={data.socials} className="justify-center" />
			</div>

			{/* Mobile bottom bar — same elements as desktop, stacked & centered:
			    copyright, then the partner logos, then the FR · EN · LN switch. */}
			<div className="mt-4 flex flex-col items-center gap-4 border-t border-white/20 pt-5 text-center text-xs xl:text-sm text-white/70">
				{/* Crédit prestataire retiré — voir SiteFooterDesktop. Les liens légaux se
				    glissent discrètement à la suite du copyright, même corps de texte. */}
				<p>
					© {new Date().getFullYear()} Banque Centrale du Congo. {t(locale, "allRightsReserved")}
					{legalLinks.map((link) => (
						<span key={link.label}>
							{" · "}
							<GuardedLink label={link.label} href={link.href} className="hover:text-white">
								{link.label}
							</GuardedLink>
						</span>
					))}
				</p>
				{languageOptions && onLanguageChange ? (
					<FooterLanguageLinks
						language={language ?? ""}
						languageOptions={languageOptions}
						onLanguageChange={onLanguageChange}
						resolveHref={resolveHref}
					/>
				) : null}
			</div>
		</div>
	);
}

/** Collapsible group section with the same chrome as the original mobile
 *  footer. Hidden when the group has no links. */
function MobileGroup({ group, fallbackTitle }: { group: FooterGroup | null; fallbackTitle: string }) {
	const { previewMode } = useDevPlaceholder();
	const links = (group?.links ?? []).filter((link) => previewMode || resolveConfiguredHref(link.href));
	if (!group || links.length === 0) return null;
	return (
		<details className="group overflow-hidden rounded-lg border border-white/15 bg-[#005CA9]" open>
			<summary className="flex cursor-pointer list-none items-center justify-between px-4 py-3 text-base xl:text-lg font-semibold text-white">
				{group.title || fallbackTitle}
				{/* Même signe que l'accordéon du menu mobile (SiteHeaderMobile) : + quand la
					    section est repliée, − quand elle est ouverte. Les deux icônes sont
					    rendues, l'état `open` du <details> décide — sans JavaScript. */}
					<div className="grid h-8 w-8 shrink-0 place-items-center rounded-full border border-white/25 bg-white/10">
						<Plus className="h-4 w-4 group-open:hidden" aria-hidden="true" />
						<Minus className="hidden h-4 w-4 group-open:block" aria-hidden="true" />
					</div>
			</summary>
			<ul className="space-y-3 border-t border-white/10 bg-[#005CA9] px-4 py-4 text-base xl:text-lg text-white/90">
				{links.map((link) => (
					<li key={link.label}>
						<GuardedLink label={link.label} className="block hover:underline" href={link.href}>
							{link.label}
						</GuardedLink>
					</li>
				))}
			</ul>
		</details>
	);
}

function pickGroup(groups: FooterGroup[], titles: string[], fallbackIndex: number): FooterGroup | null {
	for (const title of titles) {
		const match = groups.find((g) => g.title === title);
		if (match) return match;
	}
	return groups[fallbackIndex] ?? null;
}
